<template>
  <div class="column content-box custom-role-modal">
    <woot-modal-header
      :header-title="$t('CUSTOM_ROLES.ADD.TITLE')"
      :header-content="$t('CUSTOM_ROLES.ADD.DESC')"
    />

    <form class="row" @submit.prevent="addCustomRole">
      <woot-input
        v-model.trim="name"
        class="medium-12 columns"
        :placeholder="$t('CUSTOM_ROLES.FORM.NAME.PLACEHOLDER')"
        data-testid="custom-role-name"
        :maxlength="100"
        @input="onInput"
      />

      <woot-input
        v-model.trim="description"
        class="medium-12 columns"
        :placeholder="$t('CUSTOM_ROLES.FORM.DESCRIPTION.PLACEHOLDER')"
        data-testid="custom-role-description"
        :maxlength="250"
        @input="onInput"
      />

      <div class="medium-12 columns">
        <label>{{ $t('CUSTOM_ROLES.FORM.PERMISSIONS.LABEL') }}</label>
        <div class="permissions-list">
          <div
            v-for="perm in availablePermissions"
            :key="perm.key"
            class="permission-item"
          >
            <input
              :id="`perm-${perm.key}`"
              v-model="permissions[perm.key]"
              type="checkbox"
            />
            <label :for="`perm-${perm.key}`">{{ perm.label }}</label>
          </div>
        </div>
      </div>
    </form>

    <div
      class="medium-12 columns"
      style="direction: rtl; margin-bottom: 1rem; margin-right: 1rem;"
    >
      <woot-button
        :is-disabled="!canSubmit || uiFlags.isCreating"
        :is-loading="uiFlags.isCreating"
        data-testid="custom-role-submit"
        @click.prevent="addCustomRole"
      >
        {{ $t('CUSTOM_ROLES.FORM.CREATE') }}
      </woot-button>
      <woot-button class="button clear" @click.prevent="onClose">
        {{ $t('CUSTOM_ROLES.FORM.CANCEL') }}
      </woot-button>
    </div>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import { mapGetters } from 'vuex';

export default {
  mixins: [alertMixin],
  data() {
    return {
      name: '',
      description: '',
      // permissions state as map of booleans
      permissions: {},
      availablePermissions: [
        {
          key: 'conversation_manage',
          label: this.$t('CUSTOM_ROLES.PERMISSIONS.MANAGE_ALL_CONVERSATIONS'),
        },
        {
          key: 'conversation_unassigned_manage',
          label: this.$t('CUSTOM_ROLES.PERMISSIONS.MANAGE_UNASSIGNED'),
        },
        {
          key: 'conversation_participating_manage',
          label: this.$t('CUSTOM_ROLES.PERMISSIONS.MANAGE_PARTICIPATING'),
        },
        {
          key: 'contact_manage',
          label: this.$t('CUSTOM_ROLES.PERMISSIONS.MANAGE_CONTACTS'),
        },
        {
          key: 'report_manage',
          label: this.$t('CUSTOM_ROLES.PERMISSIONS.MANAGE_REPORTS'),
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'customRoles/getCustomRolesUIFlags',
    }),
    hasSelectedPermissions() {
      return Object.values(this.permissions).some(v => v === true);
    },
    canSubmit() {
      return this.name.trim().length > 0 && this.hasSelectedPermissions;
    },
  },
  mounted() {
    // default: no permissions checked when creating a new custom role
    this.availablePermissions.forEach(p => {
      this.$set(this.permissions, p.key, false);
    });
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    onInput() {},
    async addCustomRole() {
      if (!this.canSubmit) return;

      const payload = {
        name: this.name,
        description: this.description,
        permissions: Object.keys(this.permissions).filter(
          k => this.permissions[k]
        ),
      };
      try {
        // ensure permissions are strings
        payload.permissions = payload.permissions.map(p => String(p));
        await this.$store.dispatch('customRoles/create', payload);
        this.$emit('add');
        this.showAlert(this.$t('CUSTOM_ROLES.ADD.API.SUCCESS_MESSAGE'));
        this.onClose();
      } catch (error) {
        const errorMessage =
          error.message || this.$t('CUSTOM_ROLES.ADD.API.ERROR_MESSAGE');
        this.showAlert(errorMessage);
      }
    },
  },
};
</script>

<style scoped>
.custom-role-modal {
  /* give more vertical space so modal content rarely needs internal scroll */
  min-height: 520px;
  max-height: calc(85vh);
  box-sizing: border-box;
  padding-left: 1.5rem;
  padding-right: 2.5rem; /* extra right padding so scrollbar doesn't overlap close button */
  overflow: auto;
}
.permissions-list {
  display: flex;
  flex-direction: column;
}
.permission-item {
  margin: 0.4rem 0;
}
</style>
